/**
 * "Values and savings" + "Getting paid" — the two money categories.
 *
 * These carry the highest blast radius in the product: a misunderstanding here
 * bills a real client a wrong amount. Every article in this file leans harder on
 * `warn` callouts than the rest of the corpus, deliberately.
 */

import type { HelpArticle } from '../types';

export const valuesArticles: HelpArticle[] = [
  {
    slug: 'entering-values',
    title: 'Entering a value on a parcel',
    summary: 'Where the numbers go, and the small choices that decide whether your savings figure is right.',
    category: 'values',
    keywords: ['enter value', 'add value', 'record value', 'notice', 'nov', 'assessed value', 'market value', 'land', 'improvement', 'values tab'],
    related: ['what-is-a-parcel-value', 'what-is-a-reduction', 'adding-a-parcel'],
    body: [
      {
        kind: 'text',
        text: 'Values live on the parcel, under the Values tab. Each one is tagged with a tax year and a value type.',
      },
      {
        kind: 'steps',
        steps: [
          'Open the parcel and go to the Values tab.',
          'Click to add a value.',
          'Choose the tax year.',
          'Choose the value type — proposed for the assessor’s opening number, final for the agreed result.',
          'Enter the land amount and the improvement amount. The total adds itself up.',
          'Save.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Year and type are locked once saved',
        text: 'You cannot change the tax year or value type of a value after you create it. This is deliberate — those two fields are what every calculation keys off, and quietly changing one would silently rewrite history. If you picked the wrong one, delete it and add a new one.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Leave it blank if you do not know it',
        text: 'An empty box means "not told yet". A zero means "genuinely worth nothing". Typing zero for unknown will later read as a total reduction that never happened, and can produce an invoice for savings that do not exist.',
      },
    ],
  },

  {
    slug: 'exemptions',
    title: 'Exemptions and caps',
    summary: 'Rules that lower a property’s taxable value before any tax is charged, separately from anything you argue.',
    category: 'values',
    keywords: ['exemption', 'cap', 'abatement', 'circuit breaker', 'freeze', 'phase in', 'homestead', 'agricultural', 'relief', 'taxable value'],
    related: ['what-is-a-parcel-value', 'mass-updates', 'what-is-a-reduction'],
    body: [
      {
        kind: 'text',
        text: 'An exemption is a legal rule that takes some value off the top before tax is worked out. It has nothing to do with your appeal — it applies because of what the property is or who owns it.',
      },
      {
        kind: 'bullets',
        intro: 'Common examples:',
        items: [
          'Agricultural land taxed on what it produces rather than what it would sell for.',
          'Property owned by a charity or a religious organization.',
          'Caps that limit how much the taxable value is allowed to rise in one year, however far the market value moved.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'A cap can make a big reduction worth nothing',
        text: 'If a cap is already holding the taxable value down, knocking the market value down may not reduce the tax at all — the cap was doing that work already. Billing a percentage of a saving that a cap already delivered is charging the client for something you did not do. Check for a cap before you calculate a fee.',
      },
      {
        kind: 'text',
        text: 'Exemptions attach at the parcel level, and are listed on the parcel’s value records so you can see what applied in each year.',
      },
    ],
  },

  {
    slug: 'mass-updates',
    title: 'Changing many parcels at once',
    summary: 'Applying an exemption, a cap or a reduction across a whole selection of parcels in one go.',
    category: 'values',
    keywords: ['mass update', 'bulk', 'many parcels', 'apply to all', 'proposal', 'batch change', 'exemption proposal'],
    related: ['exemptions', 'importing-from-a-spreadsheet'],
    body: [
      {
        kind: 'text',
        text: 'When a jurisdiction changes a rule, it can affect hundreds of parcels at once. Mass update lets you apply that change without opening each one.',
      },
      {
        kind: 'steps',
        steps: [
          'Go to Mass update in the Government section of the left menu.',
          'Choose which parcels you are targeting.',
          'Choose what to apply.',
          'Review the proposal. It lists exactly which parcels will change and what each will become.',
          'Apply it.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Read the proposal before you apply it',
        text: 'The review step exists because this action touches many records at once and is tedious to unwind. Check the count first: if you expected around forty parcels and the proposal says six hundred, your selection is wrong. Stop and fix the filter.',
      },
    ],
  },
];

export const billingArticles: HelpArticle[] = [
  {
    slug: 'fee-agreements',
    title: 'Fee agreements — how the app knows what to charge',
    summary: 'Invoice terms hold the deal you struck with a client. Everything you bill them is calculated from this.',
    category: 'billing',
    keywords: ['invoice terms', 'fee agreement', 'contract', 'contingency', 'rate', 'flat fee', 'minimum', 'cap', 'basis', 'terms', 'pricing'],
    related: ['what-is-a-reduction', 'invoices', 'adding-a-client'],
    body: [
      {
        kind: 'text',
        text: 'An invoice term is the app’s copy of your fee agreement. Get it right once and every invoice for that client comes out right on its own.',
      },
      {
        kind: 'defs',
        intro: 'The parts of a fee agreement:',
        items: [
          { term: 'Rate', meaning: 'The percentage you charge, for a contingency deal.' },
          { term: 'Basis', meaning: 'What that percentage is a percentage of — the tax saved, or the value reduction. Read the warning below.' },
          { term: 'Flat amount', meaning: 'A fixed charge per parcel, if the deal has one.' },
          { term: 'Minimum', meaning: 'The least you will charge on a win, however small the saving.' },
          { term: 'Cap', meaning: 'The most you will charge, however big the saving.' },
          { term: 'Billing trigger', meaning: 'What has to happen before you can invoice — usually the board order arriving, sometimes the client paying their tax bill.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Basis is the field that costs real money',
        text: 'Tax savings and value reduction are different numbers, and the value reduction is many times larger. Choosing the wrong basis does not produce a slightly wrong invoice — it produces one that can be several times too high. If the agreement is ambiguous, ask the person who signed it rather than guessing.',
      },
      {
        kind: 'text',
        text: 'Fee agreements can differ between real estate and business personal property for the same client, and a client can have more than one term covering different groups of properties.',
      },
    ],
  },

  {
    slug: 'invoices',
    title: 'Creating and sending an invoice',
    summary: 'Turning finished appeal work into a bill the client actually receives.',
    category: 'billing',
    keywords: ['invoice', 'bill', 'billing', 'charge', 'send invoice', 'invoice items', 'billable', 'get paid', 'accounts receivable'],
    related: ['fee-agreements', 'invoice-items', 'quickbooks'],
    body: [
      {
        kind: 'text',
        text: 'An invoice is a group of charges sent to one client. The individual charges are called invoice items, and each one is normally the fee for a single parcel.',
      },
      {
        kind: 'steps',
        steps: [
          'Open the client, or go to Invoices in the Billing section.',
          'Create a new invoice for that client.',
          'Choose which billable items to include. These are appeals that finished and have not been billed yet.',
          'Check the total against what you expected.',
          'Send it.',
        ],
      },
      {
        kind: 'shot',
        src: '/help-shots/invoices.png',
        alt: 'The Invoices list showing invoice numbers, the client for each, the amount, the balance outstanding and the status.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'The dashboard tells you what is waiting',
        text: 'The "Billable items" card on your dashboard counts finished work nobody has invoiced. If that number keeps growing, the problem is not sales — it is that nobody is closing the loop.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'Check the total before it goes out',
        text: 'The app calculates from the fee agreement, but it can only use what it was told. If a value was entered wrong, or a cap was missed, the invoice will confidently be wrong. A quick sanity check — does this fee look like it fits the size of this win — catches most of it.',
      },
    ],
  },

  {
    slug: 'invoice-items',
    title: 'Invoice items — the individual lines',
    summary: 'One line per piece of work. This is where the calculation for each fee can be inspected.',
    category: 'billing',
    keywords: ['invoice item', 'line item', 'fee calculation', 'breakdown', 'how was this calculated', 'recalculate', 'per parcel'],
    related: ['invoices', 'fee-agreements', 'what-is-a-reduction'],
    body: [
      {
        kind: 'text',
        text: 'Each invoice item is the fee for one parcel’s result. Opening an item shows how the number was reached — the values used, the reduction, the rate applied and any cap or minimum that kicked in.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'This is the screen to open when a client queries a fee',
        text: 'Rather than recalculating by hand, open the item and read the breakdown back to them. It shows the same arithmetic the invoice was built from.',
      },
      {
        kind: 'text',
        text: 'If the underlying values change after an item was created, the item does not silently update. Recalculate it deliberately so there is a record of the change.',
      },
    ],
  },

  {
    slug: 'consultant-deals',
    title: 'Consultant deals and splits',
    summary: 'When someone outside your firm brought in the work or did some of it, and takes a share of the fee.',
    category: 'billing',
    keywords: ['consultant', 'split', 'referral', 'commission', 'share', 'partner', 'sub agent', 'revenue share', 'ledger'],
    related: ['invoices', 'fee-agreements'],
    body: [
      {
        kind: 'text',
        text: 'A consultant deal records that somebody else takes a cut of the fee on certain work — a referral partner who introduced the client, or a specialist who handled particular parcels.',
      },
      {
        kind: 'text',
        text: 'The split can be a percentage of the fee or a fixed amount. Once the deal is recorded, the app works out what is owed as invoices are raised, and the commission ledger tracks what has been paid out.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'The split comes out of your fee, not on top of it',
        text: 'The client pays the same amount either way. The deal decides how that amount is divided between your firm and the consultant.',
      },
    ],
  },

  {
    slug: 'quickbooks',
    title: 'Sending invoices to QuickBooks',
    summary: 'Connecting your accounting system so invoices and payments do not have to be typed twice.',
    category: 'billing',
    keywords: ['quickbooks', 'qbo', 'accounting', 'sync', 'integration', 'export invoices', 'bookkeeping', 'connect'],
    related: ['invoices'],
    body: [
      {
        kind: 'text',
        text: 'If your firm uses QuickBooks, invoices raised here can be pushed across so your bookkeeper is not re-keying them.',
      },
      {
        kind: 'steps',
        steps: [
          'Go to QuickBooks Sync in the Billing section.',
          'Connect the account. An admin usually does this once.',
          'Choose which invoices to send.',
          'Check that they arrived in QuickBooks before you tell anyone it is done.',
        ],
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'A sync that reports success is not proof the invoice is right',
        text: 'The sync confirms the invoice transferred. It does not confirm the amount was correct, or that it landed against the right customer. If something looks wrong in QuickBooks afterwards, fix it here first and re-sync — editing only in QuickBooks leaves the two systems disagreeing.',
      },
    ],
  },
];
